import {hpcAttrs} from '@github-ui/hpc'
import {useCallback, useMemo, useRef, useState} from 'react'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ScopedCommands} from '@github-ui/ui-commands'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {CounterLabel, IconButton} from '@primer/react'
import {clsx} from 'clsx'

import styles from './PullsInboxSection.module.css'
import {PullsInboxListItem} from './PullsInboxListItem'
import {SectionQueryPopover} from './SectionQueryPopover'
import type {DashboardSectionConfig, EnrichedPull} from './types/list-types'
import {SharedDashboardBlankslate} from '../shared/SharedDashboardBlankslate'
import {PullsInboxSectionSkeletonBody} from './PullsInboxSectionSkeleton'
import {activateFocusedRow} from '../shared/helpers/focused-row'

export interface PullsInboxSectionProps {
  config: DashboardSectionConfig
  enrichedPulls: EnrichedPull[]
  totalCount: number
  hasMore: boolean
  isPending: boolean
  isError: boolean
  isDeferredError: boolean
  loadMore: () => void
  trackHPC?: boolean
  isCollapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
  isAgentAttributionEnabled?: boolean
  /**
   * Whether the section was empty the last time it loaded (persisted across
   * reloads). Used to avoid flashing a loading skeleton while a section that we
   * already know is empty re-fetches.
   */
  knownEmpty?: boolean
  /** Number of rows rendered the last time this section loaded. */
  skeletonItemCount?: number
}

export const DEFAULT_SKELETON_ITEMS = 5

export function PullsInboxSection({
  config,
  enrichedPulls,
  totalCount,
  hasMore,
  isPending,
  isError,
  isDeferredError,
  loadMore,
  trackHPC,
  isCollapsed,
  onCollapsedChange,
  isAgentAttributionEnabled,
  knownEmpty = false,
  skeletonItemCount,
}: PullsInboxSectionProps) {
  const sectionResultLimit = 25
  const isEmpty = !isPending && totalCount === 0
  const sectionQueries = config.queries ?? []
  const showSectionQueryPopover = sectionQueries.length > 0
  const listRef = useRef<HTMLElement | undefined>(undefined)
  const activateFocusedPull = useCallback(() => activateFocusedRow(listRef.current), [])
  const scopedListCommands = useMemo(
    () => ({'list-view-items-issues-prs:open-focused-item': activateFocusedPull}),
    [activateFocusedPull],
  )

  const [emptyExpanded, setEmptyExpanded] = useState(false)
  // Treat a section we already know is empty as collapsed while it is still
  // loading, so it renders its (collapsed) empty state immediately instead of
  // flashing a skeleton that then auto-collapses once the empty result arrives.
  const appearsEmpty = isEmpty || (isPending && knownEmpty)
  const effectiveCollapsed = isCollapsed || (appearsEmpty && !emptyExpanded)

  if (isError) {
    return (
      <section aria-label={config.name}>
        <h2 className="h4 mb-2">{config.name}</h2>
        <div className={styles.listContainer}>
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      </section>
    )
  }

  // Merge-queue is only shown when it has items. While loading we don't yet know the
  // count, so keep it rendered (as a skeleton) only when actively expanded-and-pending;
  // otherwise (collapsed-while-loading, or resolved empty) hide it entirely. This
  // preserves the prior behavior where a collapsed, still-loading merge queue renders
  // nothing rather than flashing an empty header.
  if (config.id === 'merge-queue' && totalCount === 0 && !(isPending && !effectiveCollapsed)) return null

  const titleId = `pulls-inbox-section-${config.id}`
  const bodyId = `pulls-inbox-section-body-${config.id}`
  const skeletonItems = skeletonItemCount || Math.min(DEFAULT_SKELETON_ITEMS, config.maxItems)
  const collapseToggleAction = effectiveCollapsed ? 'Expand' : 'Collapse'
  const hasReachedResultLimit =
    !hasMore && enrichedPulls.length >= sectionResultLimit && totalCount >= sectionResultLimit

  return (
    <section aria-label={config.name} data-testid={`pulls-inbox-section-${config.id}`}>
      {/* Collapse-independent HPC anchor: present once the section has loaded (regardless of
          collapsed state) and absent during the skeleton, so HPC records at content-ready time
          rather than skeleton paint, and a collapsed tracked section still anchors HPC. */}
      {trackHPC && !isPending && <span hidden {...hpcAttrs()} data-testid={`pulls-inbox-section-hpc-${config.id}`} />}
      <div className={styles.sectionHeader}>
        <IconButton
          className={styles.collapseButton}
          icon={effectiveCollapsed ? ChevronRightIcon : ChevronDownIcon}
          aria-label={`${collapseToggleAction} ${config.name}`}
          description={collapseToggleAction}
          aria-expanded={!effectiveCollapsed}
          aria-controls={effectiveCollapsed ? undefined : bodyId}
          onClick={() => {
            const willExpand = effectiveCollapsed
            if (appearsEmpty) setEmptyExpanded(willExpand)
            onCollapsedChange(!effectiveCollapsed)
          }}
          variant="invisible"
          size="small"
        />
        <h2 id={titleId} className="h4">
          {config.name}
        </h2>

        {/* Keep the counter mounted while loading (reserving header space) but hidden,
            so it doesn't flash a misleading "0" before the query resolves. */}
        <span
          className={clsx(styles.count, isPending && styles.countLoading)}
          data-testid={`pulls-inbox-section-count-${config.id}`}
        >
          <CounterLabel>{totalCount}</CounterLabel>
        </span>
        {showSectionQueryPopover && (
          <SectionQueryPopover sectionName={config.name} description={config.description} queries={sectionQueries} />
        )}
      </div>
      {!effectiveCollapsed && (
        <div id={bodyId} data-testid={`pulls-inbox-section-body-${config.id}`} aria-busy={isPending}>
          {isPending ? (
            <PullsInboxSectionSkeletonBody numItems={skeletonItems} title={config.name} ariaLabelledBy={titleId} />
          ) : (
            <>
              <div className={styles.listContainer}>
                {enrichedPulls.length > 0 ? (
                  <ScopedCommands commands={scopedListCommands}>
                    <ListView
                      className="border borderColor-muted rounded-2 overflow-hidden"
                      title={config.name}
                      titleHeaderTag="h2"
                      ariaLabelledBy={titleId}
                      listRef={listRef}
                    >
                      {enrichedPulls.map(pull => (
                        <PullsInboxListItem
                          key={pull.id}
                          row={pull}
                          isAgentAttributionEnabled={isAgentAttributionEnabled}
                        />
                      ))}
                      {hasMore && (
                        <ListItem
                          className={styles.loadMoreRow}
                          title={
                            <ListItemTitle
                              value="Load more"
                              className="pt-0"
                              onClick={loadMore}
                              linkProps={{as: 'button', type: 'button', className: styles.loadMoreButton}}
                            />
                          }
                        />
                      )}
                    </ListView>
                  </ScopedCommands>
                ) : (
                  <SharedDashboardBlankslate
                    dashboardType="pull-requests"
                    variant="emptyInbox"
                    descriptionOverride={config.emptyDescription}
                  />
                )}
              </div>
              {isDeferredError && (
                <p className="f6 color-fg-muted mt-2 mb-0" role="status">
                  Some status details could not be loaded.
                </p>
              )}
              {hasReachedResultLimit && <p className="f6 color-fg-muted mt-2 mb-0">Results are limited to 25.</p>}
            </>
          )}
        </div>
      )}
    </section>
  )
}
